<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import { SideBySideGridProps } from "./SideBySideGridProps";
import { computed } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import SecondaryButton from "@/views/blocks/shared/section/buttons/secondary-button/SecondaryButton.vue";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";

const props = defineProps<{
    block: PageBlock<SideBySideGridProps>;
    pageColors: CssVarColors;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section
        :id="block.webId"
        :backgroundColor="styleValues.background"
        :scrollAnimationType="scrollAnimationType"
    >
        <FullSpacing>
            <div class="max-w-2xl lg:max-w-7xl mx-auto flex flex-col gap-16">
                <!-- Contact section -->
                <div
                    class="grid grid-cols-1 lg:grid-cols-3 gap-10"
                    v-if="
                        !dataValues.contactsTitle.isHidden ||
                        !dataValues.contactsDescription.isHidden ||
                        dataValues.contactCards.data.items.length > 0
                    "
                >
                    <div
                        v-if="
                            !dataValues.contactsTitle.isHidden ||
                            !dataValues.contactsDescription.isHidden
                        "
                        class="flex flex-col gap-y-6"
                    >
                        <EditableElement
                            v-if="!dataValues.contactsTitle.isHidden"
                            :blockId="block.id"
                            field-name="contactsTitle"
                        >
                            <Heading
                                :text="dataValues.contactsTitle.data.text"
                                :text-color="styleValues.text"
                                :tag="dataValues.contactsTitle.data.tag"
                                class="text-4xl"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.contactsDescription.isHidden"
                            :blockId="block.id"
                            field-name="contactsDescription"
                        >
                            <Paragraph
                                :text="dataValues.contactsDescription.data.text"
                                :text-color="styleValues.text"
                                class="text-base/7 opacity-70"
                            />
                        </EditableElement>
                    </div>
                    <div
                        v-if="dataValues.contactCards.data.items.length > 0"
                        class="grid grid-cols-1 sm:grid-cols-2 gap-8"
                        :class="{
                            'lg:col-span-2':
                                !dataValues.contactsTitle.isHidden ||
                                !dataValues.contactsDescription.isHidden,
                            'lg:col-span-3':
                                dataValues.contactsTitle.isHidden &&
                                dataValues.contactsDescription.isHidden,
                        }"
                    >
                        <EditableElement
                            v-for="card in dataValues.contactCards.data.items"
                            :key="card.id"
                            :blockId="block.id"
                            :field-name="`contactCards_${card.id}`"
                            :hideable="false"
                        >
                            <div
                                class="flex flex-col items-start p-10 rounded-lg card-background gap-y-3"
                            >
                                <Paragraph
                                    :text="card.title"
                                    :text-color="styleValues.text"
                                    class="text-base/7 font-semibold"
                                />
                                <div class="flex flex-col gap-y-1">
                                    <SecondaryButton
                                        :href="`mailto:${card.email}`"
                                        :label="card.email"
                                        :text-color="styleValues.brand"
                                        :show-arrow="false"
                                        class="text-sm font-semibold"
                                    />
                                    <SecondaryButton
                                        :href="`tel:${card.phone}`"
                                        :label="card.phone"
                                        :text-color="styleValues.text"
                                        :show-arrow="false"
                                        class="text-sm font-semibold"
                                    />
                                </div>
                            </div>
                        </EditableElement>
                    </div>
                </div>

                <!-- Separator line -->
                <div
                    v-if="
                        (!dataValues.contactsTitle.isHidden ||
                            !dataValues.contactsDescription.isHidden ||
                            dataValues.contactCards.data.items.length > 0) &&
                        (!dataValues.locationsTitle.isHidden ||
                            !dataValues.locationsDescription.isHidden ||
                            dataValues.locationCards.data.items.length > 0)
                    "
                    class="w-full h-px"
                    :style="{
                        backgroundColor: styleValues.border,
                    }"
                ></div>

                <!-- Locations section -->
                <div
                    class="grid grid-cols-1 lg:grid-cols-3 gap-10"
                    v-if="
                        !dataValues.locationsTitle.isHidden ||
                        !dataValues.locationsDescription.isHidden ||
                        dataValues.locationCards.data.items.length > 0
                    "
                >
                    <div
                        v-if="
                            !dataValues.locationsTitle.isHidden ||
                            !dataValues.locationsDescription.isHidden
                        "
                        class="flex flex-col gap-y-4"
                    >
                        <EditableElement
                            v-if="!dataValues.locationsTitle.isHidden"
                            :blockId="block.id"
                            field-name="locationsTitle"
                        >
                            <Heading
                                :text="dataValues.locationsTitle.data.text"
                                :text-color="styleValues.text"
                                :tag="dataValues.locationsTitle.data.tag"
                                class="text-4xl"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.locationsDescription.isHidden"
                            :blockId="block.id"
                            field-name="locationsDescription"
                        >
                            <Paragraph
                                :text="dataValues.locationsDescription.data.text"
                                :text-color="styleValues.text"
                                class="text-base/7 opacity-70"
                            />
                        </EditableElement>
                    </div>
                    <div
                        v-if="dataValues.locationCards.data.items.length > 0"
                        class="grid grid-cols-1 sm:grid-cols-2 gap-8"
                        :class="{
                            'lg:col-span-2':
                                !dataValues.locationsTitle.isHidden ||
                                !dataValues.locationsDescription.isHidden,
                            'lg:col-span-3':
                                dataValues.locationsTitle.isHidden &&
                                dataValues.locationsDescription.isHidden,
                        }"
                    >
                        <EditableElement
                            v-for="card in dataValues.locationCards.data.items"
                            :key="card.id"
                            :blockId="block.id"
                            :field-name="`locationCards_${card.id}`"
                            :hideable="false"
                        >
                            <div class="flex flex-col p-10 rounded-lg card-background gap-y-3">
                                <Paragraph
                                    :text="card.city"
                                    :text-color="styleValues.text"
                                    class="text-base/7 font-semibold"
                                />
                                <div class="flex flex-col gap-y-1">
                                    <Paragraph
                                        :text="card.addressLine1"
                                        :text-color="styleValues.text"
                                        class="text-sm opacity-70"
                                    />
                                    <Paragraph
                                        :text="card.addressLine2"
                                        :text-color="styleValues.text"
                                        class="text-sm opacity-70"
                                    />
                                </div>
                            </div>
                        </EditableElement>
                    </div>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>

<style scoped>
.card-background {
    background-color: color-mix(in oklab, oklch(70.7% 0.022 261.325) 5%, transparent);
}
</style>
